/**
 * Terms of Use v1.0 — Yan-Vada (යන්-වාද).
 *
 * Six-section terms. SL-law grounded. ETA 2006 electronic acceptance.
 * Placeholder copy in Sri Lankan English register (restrained, direct).
 * [REVIEW] markers at sections requiring external counsel.
 *
 * Version: 1.0
 * Last updated: 23 April 2026
 * Status: Draft — pending Steering Review SR-1→SR-4 + Law Stress Test LST-1→LST-2
 */

export interface TermsSection {
  id: string
  num: string
  title: string
  body: string[]
  shanakaReview?: boolean
}

const TERMS_SECTIONS: TermsSection[] = [
  {
    id: 'pitch',
    num: '1',
    title: 'The pitch page is for you.',
    body: [
      'If we sent you the link to a sponsor proposal, it is for you. Don\'t forward it to anyone who wasn\'t included in the original message. Don\'t screenshot the pricing or package details for public consumption — no LinkedIn posts, no group chat shares, no Twitter threads.',
      'If there\'s someone else at your organisation who needs to see this to make a decision, tell us and we will send them their own copy with a separate acceptance flow.',
      'This applies to all pages under vada.yan.lk/[sponsor-name].',
    ],
    shanakaReview: false,
  },
  {
    id: 'content',
    num: '2',
    title: 'What you can and can\'t do with the content.',
    body: [
      'You can read it. You can quote parts of it privately to your team or decision-makers during internal evaluation. You can think about it.',
      'You cannot republish it, repost it, or reformat it for distribution on any platform. You cannot train AI language models or image generators on the content without our written permission. You cannot translate it into another language and redistribute it without asking us first.',
      'The words, structure, pricing, and design of every page here belong to us. Ask if you want to do something more than read.',
      '[REVIEW] — AI training prohibition: confirm enforceability under SL copyright law (Intellectual Property Act No. 36 of 2003 as amended). Consider adding specific statutory reference.',
    ],
    shanakaReview: true,
  },
  {
    id: 'account',
    num: '3',
    title: 'Your account.',
    body: [
      'One person per account. Don\'t share your sign-in link — we will notice unusual login patterns from multiple locations and may temporarily suspend access.',
      'If someone else gets onto your email (device left unlocked, password shared), tell us immediately and we will invalidate all active sessions so you can reset.',
      'You can delete your account at any time from the member area. Signed agreements (like the NDA you accepted) remain in force even after account deletion — they exist independently of your login.',
    ],
    shanakaReview: false,
  },
  {
    id: 'liability',
    num: '4',
    title: 'Liability, in plain language.',
    body: [
      'We have tried to make everything on this site accurate. If something breaks — a link doesn\'t work, a number is wrong, a page won\'t load — tell us and we will fix it promptly.',
      'We are not liable for indirect or consequential losses arising from the site being temporarily unavailable, an error in the published pricing, or delays in responding to your messages.',
      'If we publish a price or term in error (typo, outdated figure), we will honour it in good faith for any sponsor who accepted it before the correction was published.',
      'Our total liability to you under or in connection with these terms shall not exceed the amount you paid us (if anything), or LKR 100,000 whichever is lower.',
      '[REVIEW] — Liability cap validity under SL contract law. LKR 100k cap reasonableness for expected sponsorship values (up to LKR 1M). Consider tiered cap by package value.',
    ],
    shanakaReview: true,
  },
  {
    id: 'changes',
    num: '5',
    title: 'Changes.',
    body: [
      'We may update these terms from time to time. Material changes — things that affect your rights, add new restrictions, or change pricing — will be sent by email to your registered address if you have an account.',
      'Small edits — fixing typos, updating dates, correcting links — will just bump the version number and date at the bottom of this page. We won\'t email you for those.',
      'Continuing to use the site after changes are posted means you accept the updated terms.',
      'The version history is maintained at the bottom of this page.',
    ],
    shanakaReview: false,
  },
  {
    id: 'law',
    num: '6',
    title: 'Applicable law.',
    body: [
      'These terms are governed by the laws of the Democratic Socialist Republic of Sri Lanka.',
      'Any dispute arising from or relating to these terms or your use of vada.yan.lk shall be subject to the exclusive jurisdiction of the courts of Colombo, Sri Lanka.',
      'Before initiating formal proceedings, both parties agree to attempt good-faith negotiation for at least 30 days.',
      'If you are an international sponsor, alternative jurisdiction (e.g., Singapore International Arbitration Centre) can be negotiated as part of your sponsorship agreement — that document governs instead of this clause for cross-border disputes.',
      '[REVIEW] — Mandatory mediation/arbitration pre-condition: confirm enforceability under SL civil procedure. International arbitration carve-out for non-SL sponsors needs clear triggering condition.',
    ],
    shanakaReview: true,
  },
  {
    id: 'acceptance',
    num: '7',
    title: 'Acceptance.',
    body: [
      'By ticking the checkbox to accept the Terms of Use (or the NDA checkbox before viewing a pitch), you confirm that:',
      'You have read and understood the relevant document in its entirety;',
      'You agree to be bound by its terms as a valid and binding agreement;',
      'Your electronic acceptance (checkbox click) constitutes valid acceptance under the Electronic Transactions Act No. 19 of 2006 of Sri Lanka;',
      'You are authorised to accept on behalf of yourself or, if applicable, the organisation you represent;',
      'If you do not agree, you may close this tab — no harm done, no obligation created.',
      '[REVIEW] — Duplicate of NDA-SP §8 acceptance clause. Consider consolidating into single "Electronic Acceptance" reference doc that both T&C and NDA point to. Confirm clickwrap sufficiency for contractual terms (not just NDAs) under SL jurisprudence.',
    ],
    shanakaReview: true,
  },
]

/**
 * Render the full Terms of Use as a React component.
 */
export function TermsPolicyNext() {
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="legal-page">
      <div className="legal-page-header">
        <span className="legal-header-badge">
          <span className="legal-dot" /> Yan-Vada · legal
        </span>
        <span className="legal-eyebrow">Terms of Use · v1.0</span>
        <h1 className="legal-page-title">The ground rules for reading this pitch.</h1>
        <p className="legal-page-subtitle">
          We tried to write this in the same voice as the rest of the site. If you&apos;ve read our pitch
          page you&apos;ve already read half of these.
        </p>
      </div>

      <div className="legal-placeholder">
        Placeholder · OpenCode / Vidura to finalise · [REVIEW]
      </div>

      <div className="legal-layout">
        <nav className="legal-toc">
          <span className="legal-toc-label">Contents</span>
          <ol className="legal-toc-list">
            {TERMS_SECTIONS.map(s => (
              <li key={s.id}>
                <button
                  className="legal-toc-link"
                  onClick={() => scrollToSection(`section-${s.id}`)}
                >
                  §{s.num} {s.title}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <div className="legal-content">
          {TERMS_SECTIONS.map(s => (
            <section key={s.id} id={`section-${s.id}`} className="legal-section">
              <div className="legal-section-num">§{s.num}</div>
              <h2 className="legal-section-title">{s.title}</h2>
              {s.shanakaReview && (
                <span className="shanaka-marker">[REVIEW]</span>
              )}
              <div className="legal-section-body">
                {s.body.map((para, i) => (
                  <p key={i} className="legal-p">{para}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      <footer className="legal-version-footer">
        Version 1.0 · Last updated 23 April 2026 ·{' '}
        <a href="mailto:hello@yan.lk" className="legal-link">hello@yan.lk</a>
      </footer>
    </div>
  )
}

export { TERMS_SECTIONS }
